import { InputHTMLAttributes, ReactNode, forwardRef, useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/utils';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string | { message?: string } | null;
    helper?: string;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
    /** Fill the wrapper to 100% width */
    fullWidth?: boolean;
    /** Shows a required marker next to the label */
    required?: boolean;
}

/**
 * Input — Unified text field with consistent focus ring, error border, and icon slots.
 * Compatible with React Hook Form via `register()` spread.
 *
 * @example
 * <Input label="Title" leadingIcon={<Pencil size={16} />} {...register('title')} error={errors.title} />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            error,
            helper,
            leadingIcon,
            trailingIcon,
            fullWidth = true,
            className,
            id,
            type,
            required,
            'aria-describedby': describedBy,
            'aria-invalid': ariaInvalid,
            ...rest
        },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        const generatedId = useId();
        const errorMsg = typeof error === 'string' ? error : (error as any)?.message;
        const inputId = id || label?.toLowerCase().replace(/\s+/g, '-') || generatedId;
        const errorId = `${inputId}-error`;

        const isPassword = type === 'password';
        const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

        return (
            <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
                {label && (
                    <label htmlFor={inputId} className="text-xs font-bold text-slate-600 select-none">
                        {label}
                        {required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                )}
                <div className="relative flex items-center">
                    {leadingIcon && (
                        <span className="absolute left-3 text-slate-400 pointer-events-none">
                            {leadingIcon}
                        </span>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        type={inputType}
                        required={required}
                        aria-invalid={ariaInvalid ?? (errorMsg ? true : undefined)}
                        aria-describedby={[describedBy, errorMsg && errorId].filter(Boolean).join(' ') || undefined}
                        className={cn(
                            'w-full h-10 rounded-xl bg-white border text-sm text-slate-900 placeholder:text-slate-400',
                            'transition-all duration-150 outline-none',
                            'focus-visible:ring-4 focus-visible:ring-teal-primary/20 focus-visible:border-teal-primary',
                            errorMsg ? 'border-red-400 focus:ring-red-300' : 'border-slate-200',
                            leadingIcon ? 'pl-9' : 'pl-3',
                            (trailingIcon || isPassword) ? 'pr-9' : 'pr-3',
                            'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed',
                            className
                        )}
                        {...rest}
                    />
                    {isPassword ? (
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    ) : (
                        trailingIcon && (
                            <span className="absolute right-3 text-slate-400 pointer-events-none">
                                {trailingIcon}
                            </span>
                        )
                    )}
                </div>
                {errorMsg && (
                    <p id={errorId} role="alert" className="text-xs font-medium text-red-500 flex items-center gap-1">
                        {errorMsg}
                    </p>
                )}
                {!errorMsg && helper && (
                    <p className="text-xs text-slate-400">{helper}</p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';
